import { useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GuideCard } from '@todo/web-ui/components/guide-card';
import { CityHotels } from '@todo/web-ui/components/hotels';
import { NavLink } from '@todo/web-ui/components/nav-link';
import { NotFoundScreen } from '@todo/web-ui/components/not-found-screen';
import { Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { PortableTextBody } from '@todo/web-ui/components/portable-text';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { localePath } from '@todo/web-ui/lib/format';
import {
  ARTICLE_BUCKET,
  GUIDES_PATH,
  articleKey,
  guideAlternates,
  guideImageAlt,
  guideLocaleLinks,
  guidePath,
  guidesCrumb,
  seededGuides,
} from '@todo/web-ui/lib/guides';
import { prefetchLists } from '@/lib/prefetch';
import { useArticle } from '@todo/core/hooks/use-article';
import { useArticles } from '@todo/core/hooks/use-articles';
import { formatDateMedium } from '@todo/core/i18n/format';
import { LocaleLinksProvider } from '@todo/core/i18n/locale-links-context';
import { isSupportedLocale, type Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { articlesIndexSeedKey } from '@todo/core/list-queries';
import { urlForImage } from '@todo/core/sanity/client';
import type { ArticleDetail } from '@todo/core/sanity/queries';
import {
  SITE_NAME,
  SITE_URL,
  absoluteUrl,
  breadcrumbJsonLd,
  portableTextToPlain,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { getPrefetched, prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';
import { SITE } from '@site';

import css from './guides.module.css';

/** The "More guides" block under the article. */
const MORE_GUIDES = 3;
/** Social-share crop of the cover (1.91:1). */
const OG_WIDTH = 1200;
const OG_HEIGHT = 630;
const WORDS_PER_MINUTE = 200;

// One page per article and language, each at its own localized slug. NOTE the
// argument: expo-router passes `{ params: parentParams }` (the riviera bug
// documented in apps/riviera/src/lib/detail/article-detail.ts).
export async function generateStaticParams({
  params,
}: {
  params: { lang?: string };
}): Promise<{ lang: string; slug: string }[]> {
  await prefetchLists();
  const locale: Locale = isSupportedLocale(params.lang) ? params.lang : 'en';
  return seededGuides(locale).map((a) => ({ lang: locale, slug: a.slug }));
}

export default function GuidePage() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { locale } = useLocale();
  // The build's record (baked into the page for the client's first render);
  // a dev server, which has none, fetches it.
  const [prefetched] = useState(() =>
    slug ? getPrefetched<ArticleDetail>(ARTICLE_BUCKET, articleKey(locale, slug)) : undefined,
  );
  const { article: fetched, loading } = useArticle(prefetched ? undefined : slug, locale);
  const article = prefetched ?? fetched;

  if (!article) return loading ? <Page>{null}</Page> : <NotFoundScreen />;
  return <GuideScreen article={article} locale={locale} />;
}

function GuideScreen({ article, locale }: { article: ArticleDetail; locale: Locale }) {
  const { t } = useTranslation();
  const path = guidePath(article.slug);
  const canonical = absoluteUrl(locale, path);
  const plain = portableTextToPlain(article.body);
  const description = truncate(article.seoDescription?.trim() || plain || article.title);
  const cover = article.mainImage;
  const heroUrl = cover ? urlForImage(cover).width(1600).height(900).url() : null;
  const ogImage = cover ? urlForImage(cover).width(OG_WIDTH).height(OG_HEIGHT).url() : null;
  const alt = guideImageAlt(article);
  const published = article.publishedAt ?? article._createdAt;
  const modified = article._updatedAt ?? published;
  const minutes = Math.max(1, Math.round(plain.split(/\s+/).filter(Boolean).length / WORDS_PER_MINUTE));
  const topics = (article.topics ?? []).map((topic) => t(`guides.topics.${topic}`, { defaultValue: '' })).filter(Boolean);
  const crumb = guidesCrumb(locale);

  // The build's seed (no refetch); a dev server, which has none, fetches the list.
  const { articles } = useArticles(locale);
  const more = articles.filter((a) => a._id !== article._id).slice(0, MORE_GUIDES);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    url: canonical,
    mainEntityOfPage: canonical,
    inLanguage: locale,
    // 16:9, 4:3 and 1:1, so any surface can use it uncropped (as on riviera).
    ...(cover
      ? {
          image: [
            [1200, 675],
            [1200, 900],
            [1200, 1200],
          ].map(([w, h]) => urlForImage(cover).width(w).height(h).url()),
        }
      : {}),
    ...(published ? { datePublished: published } : {}),
    ...(modified ? { dateModified: modified } : {}),
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: { '@id': `${SITE_URL}/#organization` },
    description,
    ...(topics[0] ? { articleSection: topics[0] } : {}),
    ...(topics.length ? { keywords: topics.join(', ') } : {}),
    ...(article.cities?.length
      ? { about: [{ '@type': 'Place', name: SITE.regionName[locale], url: absoluteUrl(locale) }] }
      : {}),
  };

  return (
    <LocaleLinksProvider value={guideLocaleLinks(article, locale)}>
      <Page>
        <PageHead
          title={seoTitle(article.seoTitle?.trim() || article.title)}
          description={description}
          path={path}
          image={ogImage}
          alternates={guideAlternates(article, locale)}
          ogType="article"
          indexable>
          {published ? <meta property="article:published_time" content={published} /> : null}
          {modified ? <meta property="article:modified_time" content={modified} /> : null}
          {topics[0] ? <meta property="article:section" content={topics[0]} /> : null}
          {ogImage ? <meta property="og:image:width" content={String(OG_WIDTH)} /> : null}
          {ogImage ? <meta property="og:image:height" content={String(OG_HEIGHT)} /> : null}
          {ogImage ? <meta property="og:image:alt" content={alt} /> : null}
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbJsonLd(locale, [crumb, { name: article.title }]))}
          </script>
          {/* Baked record, so the first client render matches this static HTML. */}
          <script id={prefetchElementId(ARTICLE_BUCKET, articleKey(locale, article.slug))} type="application/json">
            {serializePrefetch(article)}
          </script>
          {seedScript(articlesIndexSeedKey(locale))}
        </PageHead>

        <article className={css.article}>
          <nav aria-label={t('a11y.breadcrumb')} className={css.crumbs}>
            <NavLink href={localePath(locale)} className={css.crumb}>
              {t('nav.home')}
            </NavLink>
            <NavLink href={localePath(locale, GUIDES_PATH)} className={css.crumb}>
              {crumb.name}
            </NavLink>
          </nav>
          <header className={css.header}>
            <span className={css.eyebrow}>{topics[0] ?? t('guides.badge')}</span>
            <h1 className={css.title}>{article.title}</h1>
            <p className={css.meta}>
              {published ? <time dateTime={published}>{formatDateMedium(published, locale)}</time> : null}
              {published ? ' · ' : null}
              {t('guides.readingTime', { count: minutes })}
            </p>
          </header>
          {heroUrl ? (
            // The LCP element: a raw eager <img> (root AGENTS.md).
            <img
              className={css.hero}
              src={heroUrl}
              alt={alt}
              width={1600}
              height={900}
              fetchPriority="high"
              decoding="async"
            />
          ) : null}
          <PortableTextBody value={article.body} />
          {/* "Where to stay": the article's first city (on this site, the one city). */}
          <CityHotels cityId={article.cities?.[0] ?? SITE.cityIds[0]} campaign="article" pageId={article._id} areasPath="hotels" />
        </article>

        {more.length ? (
          <Section
            id="more-guides"
            title={t('guides.more')}
            more={{ href: localePath(locale, GUIDES_PATH), label: t('guides.all') }}>
            <CardGrid>
              {more.map((a) => (
                <GuideCard key={a._id} article={a} />
              ))}
            </CardGrid>
          </Section>
        ) : null}
      </Page>
    </LocaleLinksProvider>
  );
}
